import React, { ReactElement, useEffect, useMemo, useState } from "react";
import "./AboutUs.css";
import GeneralLayoutWithDots from "./components/GeneralLayoutWithDots";
import { getActiveCouncils, ICouncil } from "../../Services/Council";
import { getCountryName } from "../../utils/formatNames";
import { specialCountries } from "../../utils/const";
import ProfilePhoto from "../../components/Atomos/ProfilePhoto";
import { resolvePersonDisplayTitle } from "../../utils/personDisplayTitle";
import { resolveProfilePictureDisplayUrl } from "../../utils/profilePictureUrl";
import {
  buildFirmProfilePath,
  buildPersonProfilePath,
} from "../../utils/publicProfileUrls";
// Enhanced Council type with joined Person and Firm data
interface CouncilWithJoinedData extends ICouncil {
  person?: {
    id?: string;
    photo: string;
    name: string;
    lastName: string;
    firmEmail: string;
    title: string;
    unlistedTitle: string;
    listTitle: string;
    firmName: string;
    mainOfficeName: string;
  };
  firm?: {
    legacyId: string;
    country: string;
    firmServiceType: string;
    state: string;
  };
}

// Regional mapping as per requirements
const regionMapping: Record<
  string,
  { anchor: string; groupId: string; practiceLink: string }
> = {
  "Africa & Middle East": {
    anchor: "#africa-middle-east",
    groupId: "2764",
    practiceLink: "wsg_practicemembers.asp?group=2764&type=private",
  },
  "Asia Pacific": {
    anchor: "#asia-pacific",
    groupId: "2765",
    practiceLink: "wsg_practicemembers.asp?group=2765&type=private",
  },
  Europe: {
    anchor: "#europe",
    groupId: "2767",
    practiceLink: "wsg_practicemembers.asp?group=2767&type=private",
  },
  "Latin America": {
    anchor: "#latin-america",
    groupId: "2768",
    practiceLink: "wsg_practicemembers.asp?group=2768&type=private",
  },
  "North America & Caribbean": {
    anchor: "#north-america",
    groupId: "2769",
    practiceLink: "wsg_practicemembers.asp?group=2769&type=private",
  },
};

// Hardcoded region array as per requirements
const arrayRegions = [
  "Africa & Middle East",
  "Asia Pacific",
  "Caribbean",
  "Europe",
  "Latin America",
  "Middle East",
  "North America & Caribbean",
];

// Map region numbers to region names (based on the original SQL query structure)
const regionNumberToName: Record<number, string> = {
  1: "Africa & Middle East",
  2: "Asia Pacific",
  3: "Caribbean",
  4: "Europe",
  5: "Latin America",
  6: "Middle East",
  7: "North America & Caribbean",
};

export default function Council(): ReactElement {
  const [allCouncils, setAllCouncils] = useState<CouncilWithJoinedData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCouncilData = async () => {
      try {
        setLoading(true);
        const activeCouncils =
          (await getActiveCouncils()) as CouncilWithJoinedData[];
        setAllCouncils(activeCouncils);
      } catch (error) {
        console.error("Error fetching Council data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCouncilData();
  }, []);

  const councilsByRegion = useMemo(() => {
    const grouped = allCouncils.reduce(
      (acc, council) => {
        const regionName = regionNumberToName[council.Region];
        if (regionName) {
          if (!acc[regionName]) {
            acc[regionName] = [];
          }
          acc[regionName].push(council);
        }
        return acc;
      },
      {} as Record<string, CouncilWithJoinedData[]>,
    );

    Object.keys(grouped).forEach((region) => {
      grouped[region].sort((a, b) => {
        if (a.IsChair !== b.IsChair) {
          return b.IsChair ? 1 : -1;
        }
        if (a.IsChairEmeritus !== b.IsChairEmeritus) {
          return b.IsChairEmeritus ? 1 : -1;
        }
        const aLastName = a.person?.lastName || "";
        const bLastName = b.person?.lastName || "";
        return aLastName.localeCompare(bLastName);
      });
    });

    return grouped;
  }, [allCouncils]);

  return (
    <div className="w-full">
      <GeneralLayoutWithDots subTitle="More About WSG" CurrentPage="Council">
        <>
          {/* Council Content Section */}
          <div className="about-us-page-content py-8">
            {/* Main Content - Full Width */}
            <div className="w-full">
              {/* Regional Navigation Bar */}
              <div className="mb-4 -mt-4">
                <div className="flex flex-wrap gap-4 text-sm">
                  {arrayRegions.map((region, index) => {
                    const mapping = regionMapping[region];
                    if (mapping) {
                      return (
                        <React.Fragment key={region}>
                          <button
                            className="text-[#474F56] !text-[16px] md:!text-[17px] lg:!text-[18px] font-bold hover:text-red-600 transition-colors cursor-pointer bg-transparent border-none p-0 underline-left-to-right"
                            onClick={() => {
                              const element = document.getElementById(
                                mapping.anchor.substring(1),
                              );
                              if (element) {
                                element.scrollIntoView({
                                  behavior: "smooth",
                                  block: "start",
                                });
                              }
                            }}
                          >
                            {region}
                          </button>
                          {index < arrayRegions.length - 1 && (
                            <span className="text-gray-400 ">|</span>
                          )}
                        </React.Fragment>
                      );
                    }
                    return null;
                  })}
                </div>
              </div>

              {/* Regional Sections */}
              {
                <>
                  {arrayRegions.map((region, index) => {
                    const mapping = regionMapping[region];
                    const regionCouncils = councilsByRegion[region] || [];

                    if (regionCouncils.length === 0) {
                      return null;
                    }

                    return (
                      <div key={region} className="mb-2">
                        <hr
                          id={mapping?.anchor?.substring(1)}
                          className={`border-gray-100 mt-4 ${index === 0 ? "mb-8" : "mb-16"}`}
                        />
                        <h2 className="council-region-title mb-8 text-left">
                          {region}
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                          {regionCouncils
                            .filter(
                              (council) =>
                                council.person && council.person.name !== "N/A",
                            )
                            .map((council, index) => {
                              const personPath = buildPersonProfilePath(
                                council.person?.name,
                                council.person?.lastName,
                                council.ContactId,
                              );
                              const firmName = council.person?.firmName;
                              const firmPath = buildFirmProfilePath(
                                firmName,
                                council.firm?.legacyId,
                              );
                              return (
                                <div
                                  key={council.id || index}
                                  className="flex items-start space-x-4"
                                >
                                  <div className="w-[90px] h-[90px] rounded-full flex-shrink-0 overflow-hidden flex items-center justify-center">
                                    <ProfilePhoto
                                      src={
                                        council.person?.id
                                          ? resolveProfilePictureDisplayUrl(
                                              council.person.id,
                                              council.person.photo,
                                              { width: 90, height: 90 },
                                            )
                                          : ""
                                      }
                                      alt={`${council.person!.name} ${council.person!.lastName}`}
                                      imgClassName="w-full h-full object-cover object-[center_10%]"
                                    />
                                  </div>
                                  <div className="flex-1">
                                    {/* Position Label (if Chair or Chair Emeritus) */}
                                    {(council.IsChair ||
                                      council.IsChairEmeritus) && (
                                      <p className="council-chair mb-1 text-left">
                                        {council.IsChair
                                          ? "Chair"
                                          : "Chair Emeritus"}
                                      </p>
                                    )}

                                    {/* Name */}
                                    <div className="flex gap-2 items-start">
                                      {personPath ? (
                                        <a
                                          href={personPath}
                                          className="council-name hover:text-red-600 hover:underline transition-colors text-left leading-5"
                                        >
                                          {`${council.person?.name} ${council.person?.lastName}`}
                                        </a>
                                      ) : (
                                        <span className="council-name text-left leading-5">
                                          {council.person
                                            ? `${council.person.name} ${council.person.lastName}`
                                            : "N/A"}
                                        </span>
                                      )}

                                      {council.person?.firmEmail && (
                                        <>
                                          <div className="w-[1px] h-4 bg-black mt-[3px]"></div>
                                          <button
                                            onClick={() =>
                                              window.open(
                                                `mailto:${council.person?.firmEmail}`,
                                                "_blank",
                                              )
                                            }
                                            className="w-5 h-5 bg-blue-600 text-white rounded-full hover:bg-red-600 transition-colors flex items-center justify-center flex-shrink-0 mt-[1px]"
                                            title={`Email ${council.person.name} ${council.person.lastName}`}
                                          >
                                            <svg
                                              className="w-3 h-3"
                                              fill="currentColor"
                                              viewBox="0 0 20 20"
                                            >
                                              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                            </svg>
                                          </button>
                                        </>
                                      )}
                                    </div>

                                    {/* Title */}
                                    <p className="mb-2 text-left italic !font-[400] text-[#474F56] text-[17px] font-roboto">
                                      {resolvePersonDisplayTitle(
                                        council.person ?? {},
                                      )}
                                    </p>

                                    {/* Firm Info Group */}
                                    <div className="space-y-0">
                                      {/* Firm */}
                                      {firmPath ? (
                                        <a
                                          href={firmPath}
                                          className="council-firm hover:text-red-600 hover:underline transition-colors text-left block"
                                        >
                                          {firmName}
                                        </a>
                                      ) : (
                                        <span className="council-firm text-left block">
                                          {firmName || "World Services Group"}
                                        </span>
                                      )}

                                      {/* Service Type */}
                                      <p className="council-services text-left">
                                        {council.firm?.firmServiceType ===
                                        "Other"
                                          ? "Legal Services"
                                          : council.firm?.firmServiceType ||
                                            "Legal Services"}
                                      </p>

                                      {/* Location */}
                                      {council.firm?.country && (
                                        <p className="council-address text-left italic">
                                          {specialCountries.includes(
                                            council.firm.country,
                                          )
                                            ? council.firm.state +
                                              ", " +
                                              getCountryName(
                                                council.firm.country,
                                              )
                                            : getCountryName(
                                                council.firm.country,
                                              )}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                        </div>

                        {/* Regional Council Link */}
                        <div className="mt-8 text-right">
                          <a
                            href={mapping?.practiceLink || "#"}
                            className="council-practice-link inline-flex items-center gap-2"
                          >
                            WSG {region} Regional Council
                            <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                              <svg
                                className="w-4 h-4 text-white"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                          </a>
                        </div>
                        {index === arrayRegions.length - 1 && (
                          <hr
                            id={mapping?.anchor?.substring(1)}
                            className="border-gray-100 mb-4 scroll-mt-20 mt-4"
                          />
                        )}
                      </div>
                    );
                  })}

                  {allCouncils.length === 0 && !loading && (
                    <div className="text-center py-8">
                      <p className="text-gray-600">No council members found.</p>
                    </div>
                  )}
                </>
              }
            </div>
          </div>
        </>
      </GeneralLayoutWithDots>
    </div>
  );
}
